import { Order, Table, User, UserService } from 'app-core';
import { inject } from 'vue';
import i18n from '@/i18n';
import * as bcrypt from 'bcryptjs';
import { ICodConfirmConfig, ICodConfirmInput } from 'app-components';

const t = i18n.global.t

let codConfirm: (config: ICodConfirmConfig) => void = () => {}

/** One yes/no confirm: resolves true only on the affirmative button — "no"
 *  and dismiss both resolve false. Every named confirm below is a one-line
 *  title/message/label binding over this, so the resolve wiring lives once. */
function confirmBoolean(config: { title: string; message: string; buttonTrueText: string; buttonCancelText: string }): Promise<boolean> {
  return new Promise((resolve) => {
    codConfirm({
      ...config,
      yesHandler: () => resolve(true),
      noHandler: () => resolve(false),
      cancelHandler: () => resolve(false)
    })
  })
}

function confirmFiscalizedOrderUpdate(): Promise<boolean> {
  return confirmBoolean({
    title: t('general.confirm'),
    message: t('order.fiscalized_order_update_warning'),
    buttonTrueText: t('general.confirm'),
    buttonCancelText: t('general.back'),
  })
}

function confirmGridShrink(): Promise<boolean> {
  return confirmBoolean({
    title: t('general.confirm'),
    message: t('order.button_grid_shrink_warning'),
    buttonTrueText: t('general.confirm'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Kraj dana closes the terminal's batch: receipts inside it can no longer be reversed there. */
function confirmCloseWorkingDay(): Promise<boolean> {
  return confirmBoolean({
    title: t('general.end_of_day'),
    message: t('general.end_of_day_confirm'),
    buttonTrueText: t('general.confirm'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Price mode: staged price changes would be lost (leaving, switching back to the list, Poništi). */
function confirmDiscardPriceChanges(count: number): Promise<boolean> {
  return confirmBoolean({
    title: t('products.price_mode.discard_title'),
    message: t('products.price_mode.discard_message', { count }),
    buttonTrueText: t('products.price_mode.discard'),
    buttonCancelText: t('general.back'),
  })
}

/** Price mode: prices that more than doubled or dropped below half, listed before they go live.
 *  True saves them all; false goes back to fix them. */
function confirmUnusualPrices(rows: { name: string, from: string, to: string }[]): Promise<boolean> {
  const escape = (text: string) => text.replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`)
  const list = rows.map(row => `<li><b>${escape(row.name)}</b>: ${escape(row.from)} → <b>${escape(row.to)}</b></li>`).join('')

  return confirmBoolean({
    title: t('products.price_mode.unusual_title'),
    message: `<div><p>${escape(t('products.price_mode.unusual_message'))}</p><ul class="mt-2 list-disc pl-5">${list}</ul></div>`,
    buttonTrueText: t('products.price_mode.save_all'),
    buttonCancelText: t('general.fix'),
  })
}

/** Hospitality edit mode: counted non-destructive shrink (admin editor parity —
 *  hidden buttons keep their coords and reappear when the grid grows back). */
function confirmSalesGridShrink(count: number): Promise<boolean> {
  return confirmBoolean({
    title: t('order_settings.heading'),
    message: t('order_settings.shrink_confirm', { count }),
    buttonTrueText: t('general.yes'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Tables edit mode: sector delete states its permanence (admin parity). */
function confirmSectorDelete(): Promise<boolean> {
  return confirmBoolean({
    title: t('general.delete'),
    message: t('tables_settings.permanently_delete_sector'),
    buttonTrueText: t('general.delete'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Tables edit mode: beach-row delete takes its sunbeds with it (admin parity). */
function confirmBeachRowDelete(): Promise<boolean> {
  return confirmBoolean({
    title: t('general.delete'),
    message: t('tables_settings.delete_row_confirm'),
    buttonTrueText: t('general.confirm'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Tables edit mode: bulk delete states its count (admin editor parity). */
function confirmMassTableDelete(count: number): Promise<boolean> {
  return confirmBoolean({
    title: t('general.delete'),
    message: t('tables_settings.confirm_mass_delete', { count }),
    buttonTrueText: t('general.delete'),
    buttonCancelText: t('general.cancel'),
  })
}

/** Hospitality edit mode: category-button remove names its cascade (the server
 *  deletes that category's product buttons with it — admin editor parity). */
function confirmCategoryButtonDelete(count: number): Promise<boolean> {
  return confirmBoolean({
    title: t('general.delete'),
    message: t('order_settings.delete_category_confirm', { count }),
    buttonTrueText: t('general.delete'),
    buttonCancelText: t('general.cancel'),
  })
}

function onProductNotFound() {
  const audio = new Audio('/audio/alert1.mp3')
  audio.play().catch(() => {})

  codConfirm({
    title: t('general.error'),
    message: t('retail.order.product_not_found'),
    buttonTrueText: t('general.continue'),
    buttonCancelText: ''
  })
}

/** Retail scan: mlkit detected a barcode but neither rawValue nor displayValue
 *  decodes to text — tell the cashier instead of closing the scanner silently.
 *  Exported for the barcode seam (a plain module, not a component): every scan
 *  starts from retail Order.vue, whose setup calls useConfirm() and so binds
 *  codConfirm before a scan can happen. */
export function onBarcodeUnreadable() {
  codConfirm({
    title: t('general.error'),
    message: t('retail.order.barcode_unreadable'),
    buttonTrueText: t('general.continue'),
    buttonCancelText: ''
  })
}

/** Approving a QR request onto a table another waiter serves: the round is
 *  issued under the approver's name on a table the approver cannot open. */
function confirmQrApproveOnForeignTable(ownerName: string, tableName: string): Promise<boolean> {
  return confirmBoolean({
    title: t('orders_hub.approve_foreign_table_title'),
    message: t('orders_hub.approve_foreign_table_message', { table: tableName, name: ownerName }),
    buttonTrueText: t('orders_hub.approve'),
    buttonCancelText: t('general.cancel'),
  })
}

function confirmTableTransfer(dstTable: Table): Promise<{ success: boolean, error?: string }> {
  const orders = Order.onTables().filter(order => order.table_id === dstTable.id)

  const dstUser = User.getTableOwner(orders)
  const curUser = User.current()

  return new Promise((resolve, reject) => {
    const inputs: ICodConfirmInput[] = []

    const shouldAskForUserPin = orders.length
      && dstUser?.id !== curUser?.id
      && !UserService.allows('allow_enter_all_tables')
      && dstTable.type !== 'limited'

    if (shouldAskForUserPin) {
      inputs.push({
        type: 'password',
        key: 'pin',
        label: t('general.pin'),
        validation: 'required'
      })
    }

    codConfirm({
      title: t('tables.transfer'),
      message: orders.length
        ? t('tables.transfer_warning_occupied_confirm') + (inputs.length ? `<br><br>${t('tables.transfer_warning_pin')}` : '')
        : t('tables.transfer_warning_confirm'),
      buttonTrueText: t('general.confirm'),
      buttonCancelText: t('general.cancel'),
      inputs,
      yesHandler: async (values: Record<string, any>) => {
        if(inputs.length) {
          if(!bcrypt.compareSync(values.pin, dstUser?.pin || '')) {
            resolve({ success: false, error: t('general.invalid_pin') })
            return
          }
        }

        resolve({ success: true })
      },
      noHandler: () => {
        resolve({ success: false })
      },
      cancelHandler: () => {
        resolve({ success: false })
      },
      canDismiss: false
    })
  })
}

export function useConfirm() {
  codConfirm = inject('codConfirm') as (config: ICodConfirmConfig) => void

  return {
    confirmTableTransfer,
    confirmQrApproveOnForeignTable,
    confirmFiscalizedOrderUpdate,
    confirmCloseWorkingDay,
    confirmGridShrink,
    confirmSalesGridShrink,
    confirmDiscardPriceChanges,
    confirmUnusualPrices,
    confirmSectorDelete,
    confirmBeachRowDelete,
    confirmMassTableDelete,
    confirmCategoryButtonDelete,
    onProductNotFound,
    onBarcodeUnreadable
  };
}
